import { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import ProviderCard from "@/components/ProviderCard";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export default function ProvidersDirectory({
  title,
  subtitle,
  fetcher,
  specialties,
  testIdPrefix,
  defaultSort = "distance",
}) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialty, setSpecialty] = useState("all");
  const [minRating, setMinRating] = useState("0");
  const [maxDistance, setMaxDistance] = useState("25");
  const [openNow, setOpenNow] = useState(false);
  const [sort, setSort] = useState(defaultSort);

  useEffect(() => {
    setLoading(true);
    const params = {
      sort,
      min_rating: parseFloat(minRating),
      max_distance: parseFloat(maxDistance),
    };
    if (specialty !== "all") params.specialty = specialty;
    if (openNow) params.open_now = true;

    fetcher(params)
      .then(setProviders)
      .finally(() => setLoading(false));
  }, [specialty, minRating, maxDistance, openNow, sort, fetcher]);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50">
          {title}
        </h1>
        <p className="mt-3 text-slate-400 max-w-2xl">{subtitle}</p>
      </header>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="filter-bar">
        <div className="flex items-center gap-2 mb-4">
          <SlidersHorizontal className="w-4 h-4 text-amber-500" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">
            Filter & Sort
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Specialty</Label>
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger data-testid="filter-specialty" className="bg-slate-950/70 border-slate-700 text-slate-100 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="all">All</SelectItem>
                {specialties.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Min rating</Label>
            <Select value={minRating} onValueChange={setMinRating}>
              <SelectTrigger data-testid="filter-rating" className="bg-slate-950/70 border-slate-700 text-slate-100 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="0">Any</SelectItem>
                <SelectItem value="4">4.0+</SelectItem>
                <SelectItem value="4.5">4.5+</SelectItem>
                <SelectItem value="4.8">4.8+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Distance</Label>
            <Select value={maxDistance} onValueChange={setMaxDistance}>
              <SelectTrigger data-testid="filter-distance" className="bg-slate-950/70 border-slate-700 text-slate-100 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="1">1 mi</SelectItem>
                <SelectItem value="5">5 mi</SelectItem>
                <SelectItem value="10">10 mi</SelectItem>
                <SelectItem value="25">25 mi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Sort by</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="filter-sort" className="bg-slate-950/70 border-slate-700 text-slate-100 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="distance">Nearest</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="response_time">Fastest Response</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end pb-1">
            <div className="flex items-center gap-2 h-10">
              <Switch
                checked={openNow}
                onCheckedChange={setOpenNow}
                data-testid="filter-open-now"
                className="data-[state=checked]:bg-amber-500"
              />
              <Label className="text-xs text-slate-300 cursor-pointer">Open now</Label>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="loading-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-80 rounded-xl border border-slate-800 bg-slate-900/40 animate-pulse" />
          ))}
        </div>
      ) : providers.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-10 text-center text-slate-400" data-testid="empty-state">
          No providers match your filters. Try widening the distance or rating.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="providers-grid">
          {providers.map((p) => (
            <ProviderCard key={p.id} provider={p} testIdPrefix={testIdPrefix} />
          ))}
        </div>
      )}
    </div>
  );
}
